import {
  proxyFetchGet,
  proxyFetchPatch,
  proxyFetchPost,
  proxyFetchPut,
  uploadFile,
} from '@/api/http';

export type EnterpriseRole = 'owner' | 'admin' | 'member';
export type EnterpriseMemberStatus = 'active' | 'disabled';
export type DepartmentStatus = 'active' | 'disabled';

export interface EnterpriseMe {
  enterprise_id: string;
  enterprise_name: string;
  member_id: string;
  department_id: string | null;
  role: EnterpriseRole;
  user_id: number;
  email: string;
  display_name: string;
}

export interface EnterpriseDepartment {
  id: string;
  enterprise_id: string;
  name: string;
  parent_id: string | null;
  status: DepartmentStatus;
}

export interface EnterpriseMember {
  id: string;
  enterprise_id: string;
  user_id: number;
  email: string;
  display_name: string;
  department_id: string | null;
  role: EnterpriseRole;
  status: EnterpriseMemberStatus;
}

export interface RouterUsage {
  period_id: string | null;
  points_limit: number | null;
  points_adjustment: number;
  points_used: number;
  points_reserved: number;
  points_remaining: number | null;
  estimated_cost_cny: string;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  internal_cost: number;
  remaining_quota: number | null;
  updated_at: string | null;
}

export interface PointRefreshPolicy {
  enterprise_id: string;
  auto_refresh_enabled: boolean;
  refresh_day: number;
  timezone: string;
  rollover_enabled: boolean;
}

export interface PointRefreshPreview {
  batch_id: string;
  enterprise_id: string;
  period_id: string;
  scope_type: 'enterprise' | 'department';
  scope_id: string | null;
  rollover_enabled: boolean;
  member_count: number;
  account_count: number;
  total_points: number;
}

export interface PointRefreshResult {
  batch_id: string;
  enterprise_id: string;
  period_id: string;
  status: string;
  member_count: number;
  account_count: number;
  total_points: number;
}

export type SkillPolicyScope = 'enterprise' | 'department' | 'employee';
export type SkillPolicyEffect = 'allow' | 'deny';
export type SkillPolicySource = 'public' | 'enterprise_private';

export interface CapabilitySkill {
  id: string;
  name: string;
  description: string;
  tools: string[];
}

export interface CapabilityPlugin {
  id: string;
  name: string;
  description: string;
  version: string;
  source: SkillPolicySource;
  skills: CapabilitySkill[];
}

export interface SkillPolicy {
  id: string;
  enterprise_id: string;
  scope_type: SkillPolicyScope;
  scope_id: string;
  plugin_id: string;
  skill_id: string;
  effect: SkillPolicyEffect;
  source: SkillPolicySource;
  created_by_user_id: number | null;
}

export interface EnterpriseBrowserPolicy {
  enterprise_id: string;
  version: number;
  allowed_domains: string[];
  blocked_domains: string[];
  updated_by_user_id: number | null;
}

export type PrivateSkillVersionStatus =
  'draft' | 'review' | 'published' | 'rejected' | 'archived';

export interface PrivateSkillVersion {
  id: string;
  skill_id: string;
  version: string;
  status: PrivateSkillVersionStatus;
  instructions: string;
  changelog: string;
  review_note: string;
  submitted_at: string | null;
  published_at: string | null;
  created_at: string | null;
}

export interface EnterprisePrivateSkill {
  id: string;
  enterprise_id: string;
  slug: string;
  name: string;
  description: string;
  created_at: string | null;
  versions: PrivateSkillVersion[];
}

export interface GitHubSkillCandidate {
  path: string;
  slug: string;
  name: string;
  description: string;
  version: string;
}

export const fetchEnterpriseMe = (): Promise<EnterpriseMe> =>
  proxyFetchGet('/api/v1/enterprise/me');

export const fetchEnterpriseDepartments = (): Promise<EnterpriseDepartment[]> =>
  proxyFetchGet('/api/v1/enterprise/departments');

export const createEnterpriseDepartment = (
  name: string,
  parentId: string | null = null
): Promise<EnterpriseDepartment> =>
  proxyFetchPost('/api/v1/enterprise/departments', {
    name: name.trim(),
    parent_id: parentId,
  });

export const fetchEnterpriseMembers = (): Promise<EnterpriseMember[]> =>
  proxyFetchGet('/api/v1/enterprise/members');

export const fetchMyRouterUsage = (): Promise<RouterUsage> =>
  proxyFetchGet('/api/v1/enterprise/me/router-usage');

export const updateEnterpriseMember = (
  memberId: string,
  update: Partial<Pick<EnterpriseMember, 'department_id' | 'role' | 'status'>>
): Promise<EnterpriseMember> =>
  proxyFetchPatch(`/api/v1/enterprise/members/${memberId}`, update);

export const fetchMemberRouterUsage = (
  memberId: string
): Promise<RouterUsage> =>
  proxyFetchGet(`/api/v1/enterprise/members/${memberId}/router-usage`);

export const fetchDepartmentRouterUsage = (
  departmentId: string
): Promise<RouterUsage> =>
  proxyFetchGet(
    `/api/v1/enterprise/departments/${departmentId}/router-usage`
  );

export const updateMemberRouterQuota = (memberId: string, quotaLimit: number) =>
  proxyFetchPut(`/api/v1/enterprise/members/${memberId}/router-quota`, {
    quota_limit: quotaLimit,
  });

export const updateDepartmentRouterQuota = (
  departmentId: string,
  quotaLimit: number
) =>
  proxyFetchPut(
    `/api/v1/enterprise/departments/${departmentId}/router-quota`,
    { quota_limit: quotaLimit }
  );

export const fetchPointRefreshPolicy = (): Promise<PointRefreshPolicy> =>
  proxyFetchGet('/api/v1/enterprise/points/refresh-policy');

export const updatePointRefreshPolicy = (input: {
  auto_refresh_enabled: boolean;
  rollover_enabled: boolean;
}): Promise<PointRefreshPolicy> =>
  proxyFetchPut('/api/v1/enterprise/points/refresh-policy', input);

export const previewPointRefresh = (input: {
  period_id: string;
  scope_type: 'enterprise' | 'department';
  scope_id: string | null;
  rollover_enabled: boolean;
}): Promise<PointRefreshPreview> =>
  proxyFetchPost('/api/v1/enterprise/points/refresh-preview', input);

export const executePointRefresh = (
  batchId: string
): Promise<PointRefreshResult> =>
  proxyFetchPost('/api/v1/enterprise/points/refresh', {
    batch_id: batchId,
  });

export const adjustMemberPoints = (
  memberId: string,
  input: { amount: number; reason: string }
): Promise<RouterUsage> =>
  proxyFetchPost(
    `/api/v1/enterprise/members/${memberId}/point-adjustments`,
    input
  );

export const fetchCapabilityCatalog = async (): Promise<CapabilityPlugin[]> => {
  const response = await proxyFetchGet('/api/v1/enterprise/capabilities');
  return Array.isArray(response?.plugins) ? response.plugins : [];
};

export const fetchSkillPolicies = (): Promise<SkillPolicy[]> =>
  proxyFetchGet('/api/v1/enterprise/skill-policies');

export const fetchBrowserPolicy = (): Promise<EnterpriseBrowserPolicy> =>
  proxyFetchGet('/api/v1/enterprise/browser-policy');

export const updateBrowserPolicy = (input: {
  allowed_domains: string[];
  blocked_domains: string[];
}): Promise<EnterpriseBrowserPolicy> =>
  proxyFetchPut('/api/v1/enterprise/browser-policy', input);

export const putSkillPolicy = (input: {
  scope_type: SkillPolicyScope;
  scope_id: string;
  plugin_id: string;
  skill_id: string;
  effect: SkillPolicyEffect;
  source: SkillPolicySource;
}): Promise<SkillPolicy> =>
  proxyFetchPut('/api/v1/enterprise/skill-policies', input);

export const fetchPrivateSkills = (): Promise<EnterprisePrivateSkill[]> =>
  proxyFetchGet('/api/v1/enterprise/private-skills');

export const createPrivateSkill = (input: {
  slug: string;
  name: string;
  description: string;
  version: string;
  instructions: string;
  changelog?: string;
}): Promise<EnterprisePrivateSkill> =>
  proxyFetchPost('/api/v1/enterprise/private-skills', input);

export const createPrivateSkillVersion = (
  skillId: string,
  input: { version: string; instructions: string; changelog?: string }
): Promise<PrivateSkillVersion> =>
  proxyFetchPost(
    `/api/v1/enterprise/private-skills/${skillId}/versions`,
    input
  );

export const submitPrivateSkillVersion = (
  versionId: string
): Promise<PrivateSkillVersion> =>
  proxyFetchPost(
    `/api/v1/enterprise/private-skill-versions/${versionId}/submit`
  );

export const publishPrivateSkillVersion = (
  versionId: string
): Promise<PrivateSkillVersion> =>
  proxyFetchPost(
    `/api/v1/enterprise/private-skill-versions/${versionId}/publish`
  );

export const rollbackPrivateSkillVersion = (
  versionId: string
): Promise<PrivateSkillVersion> =>
  proxyFetchPost(
    `/api/v1/enterprise/private-skill-versions/${versionId}/rollback`
  );

export const importPrivateSkillPackage = (
  file: File
): Promise<EnterprisePrivateSkill> => {
  const form = new FormData();
  form.append('file', file);
  return uploadFile('/api/v1/enterprise/private-skills/import', form);
};

export const importPrivateSkillFromGit = (input: {
  repository_url: string;
  ref: string;
  skill_path?: string;
}): Promise<EnterprisePrivateSkill> =>
  proxyFetchPost('/api/v1/enterprise/private-skills/import-git', input);

export const inspectPrivateSkillsFromGit = (input: {
  repository_url: string;
  ref: string;
}): Promise<GitHubSkillCandidate[]> =>
  proxyFetchPost('/api/v1/enterprise/private-skills/import-git/inspect', input);

export const updatePrivateSkillVersion = (
  versionId: string,
  input: { instructions: string; changelog: string }
): Promise<PrivateSkillVersion> =>
  proxyFetchPatch(
    `/api/v1/enterprise/private-skill-versions/${versionId}`,
    input
  );

export const rejectPrivateSkillVersion = (
  versionId: string,
  note: string
): Promise<PrivateSkillVersion> =>
  proxyFetchPost(
    `/api/v1/enterprise/private-skill-versions/${versionId}/reject`,
    { note }
  );
